import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, FileUp, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ImportStepInputProps {
  rawInput: string;
  setRawInput: (val: string) => void;
  setParsedRecipes: (recipes: any[]) => void;
  setRawLLMOutput: (val: string | null) => void;
  goToPreview: () => void;
  onOpenAssistantWithPrefill?: (prefill: string) => void;
}

// Robust, deterministic markdown parser for batch recipes (UNCHANGED)
function parseBatchRecipes(raw: string) {
  if (!raw || !raw.trim()) return [];
  
  const text = raw.replace(/\r\n?/g, '\n').trim();
  
  const recipeBlocks: string[] = [];
  const lines = text.split('\n');
  let currentBlock: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.match(/^-{3,}$/)) {
      if (currentBlock.length > 0) {
        recipeBlocks.push(currentBlock.join('\n').trim());
        currentBlock = [];
      }
      continue;
    }
    
    if (line.match(/^#{1,3}\s+/) && currentBlock.length > 0) {
      recipeBlocks.push(currentBlock.join('\n').trim());
      currentBlock = [lines[i]];
      continue;
    }
    
    currentBlock.push(lines[i]);
  }
  
  if (currentBlock.length > 0) {
    recipeBlocks.push(currentBlock.join('\n').trim());
  }
  
  return recipeBlocks.map(block => {
    const lines = block.split('\n');
    let title = '';
    let description = '';
    let ingredients = '';
    let method = '';
    let notes = '';
    let currentSection = '';
    let buffer: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const titleMatch = line.match(/^#{1,3}\s+(.+)$/);
      if (titleMatch && !title) {
        title = titleMatch[1].trim();
        continue;
      }
      
      const sectionMatch = line.match(/^(?:#{2,}\s*|\*\*)?(description|ingredients|method|notes|instructions|directions)(\*\*)?:?\s*$/i);
      if (sectionMatch) {
        if (currentSection && buffer.length > 0) {
          const content = buffer.join('\n').trim();
          if (currentSection === 'description') description = content;
          else if (currentSection === 'ingredients') ingredients = content;
          else if (currentSection === 'method') method = content;
          else if (currentSection === 'notes') notes = content;
        }
        
        const sectionName = sectionMatch[1].toLowerCase();
        currentSection = sectionName === 'instructions' || sectionName === 'directions' ? 'method' : sectionName;
        buffer = [];
        continue;
      }
      
      buffer.push(line);
    }
    
    if (currentSection && buffer.length > 0) {
      const content = buffer.join('\n').trim();
      if (currentSection === 'description') description = content;
      else if (currentSection === 'ingredients') ingredients = content;
      else if (currentSection === 'method') method = content;
      else if (currentSection === 'notes') notes = content;
    }
    
    if (title) {
      return {
        title,
        description: description || '',
        ingredients: ingredients || '',
        method: method || '',
        notes: notes || ''
      };
    }
    return null;
  }).filter(recipe => recipe !== null);
}

export default function ImportStepInput({
  rawInput,
  setRawInput,
  setParsedRecipes,
  setRawLLMOutput,
  goToPreview,
  onOpenAssistantWithPrefill,
}: ImportStepInputProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleParse = () => {
    setError(null);
    setLoading(true);
    
    try {
      const recipes = parseBatchRecipes(rawInput);
      
      if (!recipes.length) {
        setError('No valid recipes found. Ensure each recipe starts with a title like "# Recipe Title" and includes sections like "## Ingredients".');
        setParsedRecipes([]);
        setRawLLMOutput(null);
        setLoading(false);
        return;
      }
      
      const validRecipes = recipes.filter(recipe => 
        recipe.title && (recipe.ingredients || recipe.method)
      );
      
      if (validRecipes.length === 0) {
        setError('Found recipe titles but missing ingredients or method sections. Please check the format.');
        setParsedRecipes([]);
        setRawLLMOutput(null);
        setLoading(false);
        return;
      }
      
      if (validRecipes.length < recipes.length) {
        setError(`Found ${recipes.length} recipes, but ${recipes.length - validRecipes.length} are missing required content. Showing ${validRecipes.length} valid recipes.`);
      }
      
      setParsedRecipes(validRecipes);
      setRawLLMOutput(null);
      goToPreview();
    } catch {
      setError('Failed to parse recipes. Please check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileName('');
      return;
    }
    setFileName(file.name);
    const text = await file.text();
    setRawInput(text);
  };

  const handleOpenAssistant = () => {
    const msg = 'Can you help me import a recipe?';
    if (onOpenAssistantWithPrefill) {
      onOpenAssistantWithPrefill(msg);
    } else {
      const event = new CustomEvent('openAssistantWidget', {
        detail: { prefill: msg }
      });
      window.dispatchEvent(event);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Batch Import Recipes
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Upload a file or paste text to import multiple recipes at once.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/manage?tab=recipes')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Main Card */}
      <div className="bg-background/80 dark:bg-gray-900/50 border border-border/50 rounded-2xl shadow-lg p-6 sm:p-8 backdrop-blur-sm">
        
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-border/50 pb-6">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <input
              type="file"
              ref={fileInputRef}
              accept=".md,.txt,.markdown,text/markdown,text/plain"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="bg-background/50 hover:bg-background border-border/50 hover:border-border transition-all"
            >
              <FileUp className="h-4 w-4 mr-2" />
              Upload File
            </Button>
            {fileName && <span className="text-sm text-muted-foreground truncate">{fileName}</span>}
          </div>
          
          <Button 
            onClick={handleParse} 
            disabled={loading || !rawInput.trim()} 
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white font-semibold shadow-sm transition-all"
          >
            {loading ? 'Parsing...' : 'Parse & Preview'}
          </Button>
        </div>

        {/* Input Area */}
        <div className="pt-6">
          <label htmlFor="recipe-input" className="block text-sm font-medium text-muted-foreground mb-2">
            Or paste recipes directly into the editor below
          </label>
          <Textarea
            id="recipe-input"
            value={rawInput}
            onChange={e => setRawInput(e.target.value)}
            rows={15}
            placeholder="Paste recipes here, ensuring they follow the template format..."
            className="w-full font-mono text-sm bg-background/70 dark:bg-gray-900/70 border-border/60 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 transition-colors"
          />
        </div>

        {/* Error/Loading */}
        {error && <div className="mt-4 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">{error}</div>}
        {loading && (
          <div className="flex items-center justify-center gap-2 mt-4 text-blue-600 dark:text-blue-300">
            <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></span>
            <span>Parsing recipes...</span>
          </div>
        )}
      </div>

      {/* Footer Help */}
      <div className="mt-6 text-center">
        <details className="inline-block bg-muted/40 dark:bg-gray-900/40 p-3 rounded-lg text-sm">
          <summary className="cursor-pointer select-none font-medium text-foreground/80 hover:text-foreground">
            <HelpCircle className="inline h-4 w-4 mr-2" />
            Need Help with Formatting?
          </summary>
          <div className="mt-3 pt-3 border-t border-border/50 text-left">
            <p className="font-semibold mb-2 dark:text-gray-100">Batch Import Template</p>
            <pre className="text-xs bg-background/50 dark:bg-gray-800/60 border border-border/50 rounded p-3 overflow-x-auto text-foreground/90">
{`# Recipe Title
## Description
A short description here.
## Ingredients
- 1 cup flour
- 2 eggs
## Method
1. Mix ingredients
2. Bake for 20 minutes
## Notes
Any notes here.
---
### Another Recipe Title
**Description**
Another description here.`}
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              <b>Instructions:</b> Separate recipes with <code>---</code>. Each recipe must have a title (e.g., <code># Title</code>) and sections for <code>## Ingredients</code> and <code>## Method</code>.
            </p>
            <div className="mt-4 pt-3 border-t border-border/50 text-center">
                <p className="text-xs text-muted-foreground">Still having trouble? The{' '}
                <button onClick={handleOpenAssistant} className="text-blue-500 hover:underline font-semibold focus:outline-none">
                    Lab Assistant
                </button>
                {' '}can help.</p>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
